import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { IPageContent } from "api/interfaces/static-content.type";
import FAQMetaHead from "component/meta-header/FAQMetaHead";
import OrganizationMetaHeader from "component/meta-header/OrganizationMetaHeader";
import VideosMetaData from "component/meta-header/VideosMetaData";
import { RootState } from "interfaces/generics.type";
import Head from "next/head";
import { useSelector } from "react-redux";
import { getProductImage } from "util/helper/getProductImage";
import { PAGE_TYPES } from "util/product/product-constants";
import BreadcrumbsMetaHead from "../../../component/meta-header/BreadcrumbsMetaHead";
import LocalBusinessMeta from "../../../component/meta-header/LocalBusinessMeta";
import SiteLinkMeta from "../../../component/meta-header/SiteLinkMeta";
import { aggregateRatingMeta, getMetaData, getReviewMeta } from "../../../component/meta-header/meta-utils";
import { getRelativeImagePath } from "../../../util/helper/getRelativeImagePath";
import { getProductHeading } from "../../../util/product/get-product-heading";

interface IProps {
  product: ICompactProduct;
  staticContent: IPageContent;
}

function ProductMetaHead(props: IProps) {
  const {
    product,
    canonicalUrl,
    description,
    manufacturer,
    sku,
    upc,
    outOfStock,
    price,
    manufacturerSmallImageUrl,
    pageType,
    // productQuestions,
    productReviews,
    staticContent,
    breadCrumbsList,
    productImagesV2 = [],
    productImages,
    locale,
  } = props;

  const { productLineVideos = [], videos = [], manufacturerVideos = [] } = staticContent;
  // todo move individual props to product object
  const { partNumber } = product || {};

  const { currentPageType, urlInformation } = useSelector((store: RootState) => store.utils);
  const { yearRange, year } = urlInformation;

  function getInitialPageTitle() {
    if (currentPageType === PAGE_TYPES.PRODUCT_MAKE_MODEL_PAGE) {
      return `${yearRange[0]}-${yearRange[yearRange.length - 1]} ${urlInformation.make} ${urlInformation.model} `;
    }
    if (currentPageType === PAGE_TYPES.PRODUCT_FITMENT) {
      return `${year} ${urlInformation.make} ${urlInformation.model} `;
    }
    return "";
  }
  const initialPageTitle = getInitialPageTitle();

  function getInitialDescription() {
    if (currentPageType === PAGE_TYPES.PRODUCT_FITMENT) {
      return `${description} for ${initialPageTitle}`;
    }
    if (currentPageType !== PAGE_TYPES.PRODUCT_MAKE_MODEL_PAGE) {
      return "";
    }
    const startYear = yearRange[0];
    const endYear = yearRange[yearRange.length - 1];
    if (Number(endYear) - Number(startYear) + 1 <= 5) {
      const yearsText = yearRange.reduce((acc: string, el: string, idx: number) => (idx === yearRange.length - 1 ? acc + el : `${acc}${el}, `), "");
      return `${description} for ${yearsText} ${urlInformation.make} ${urlInformation.model}`;
    }
    return `${description} for ${initialPageTitle}`;
  }

  const initialPageDesc = getInitialDescription();
  const { pageTitle = "", metaDescription = "", metaKeywords = "" } = getMetaData({
    staticContent,
    description,
    partNumber,
    manufacturer,
    initialPageTitle,
    initialPageDesc,
    longDescription: product.longDescription,
    pageType: currentPageType,
  });

  const prodDesc = staticContent?.links?.contentIds?.filter((_: any) => _.uiDetails?.uiType === "PRODUCT_DESCRIPTION");
  let metaDescriptionJSONLD;
  if (prodDesc && prodDesc[0]) {
    metaDescriptionJSONLD = prodDesc[0].uiDetails.content.article;
  }

  const url = `https://partsavatar.ca${canonicalUrl}`;

  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 5);

  const isWebpImage = productImagesV2?.length > 0;
  const productImagesArr = isWebpImage ? productImagesV2 : productImages;
  const productImageUrls = (productImagesArr.find((_: any) => _.type === "SINGLE_IMAGE")?.urls || []).filter((_: any) => _ !== "");
  const relativeImage = isWebpImage ? getRelativeImagePath(`/img/ds/${productImageUrls[0]}`) : getRelativeImagePath(getProductImage(productImageUrls[0]));
  const image = process.env.NODE_ENV === "development" ? relativeImage : `${process.env.NEXT_PUBLIC_ROOT_URL}${relativeImage}`;

  const microdata = {
    "@context": "http://schema.org/",
    "@type": "Product",

    name: getProductHeading(props),
    image, // Check if we should use this property for that
    description: metaDescriptionJSONLD || metaDescription,
    sku,
    gtin13: upc,
    mpn: partNumber,
    url,
    brand: {
      "@type": "http://schema.org/Brand",
      name: manufacturer,
      logo: getRelativeImagePath(manufacturerSmallImageUrl),
    },
    offers: {
      "@type": "Offer",
      priceCurrency: "CAD",
      priceValidUntil: `${nextMonth.getFullYear()}-${nextMonth.getMonth() + 1}-05`,
      // Todo: Check where this is used
      price: (price / 100).toFixed(2),
      availability: `http://schema.org/${outOfStock ? "OutOfStock" : "InStock"}`,
      url,
      itemCondition: "https://schema.org/NewCondition",
    },
    aggregateRating: aggregateRatingMeta(productReviews),
    review: getReviewMeta(productReviews),
  };

  return (
    <>

      <Head>

        <title>{pageTitle}</title>

        <link rel="canonical" href={locale === "fr" ? `https://partsavatar.ca/fr${canonicalUrl}` : url} />
        <link rel="alternate" hrefLang="en" href={url} />
        <link rel="alternate" hrefLang="fr" href={`https://partsavatar.ca/fr${canonicalUrl}`} />
        <link rel="alternate" hrefLang="x-default" href={url} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(microdata) }}
        />

        <script type="text/javascript" src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" async />

        <meta name="facebook-domain-verification" content="82cgyip0cz3a7igt87rjow5o8bf9th" />
        <meta name="description" content={metaDescription} />

        <meta name="keywords" content={metaKeywords.toLowerCase()} />

        {pageType === "PARTS_PRODUCT_PAGE" && <meta name="robots" content="noindex" />}

        {pageType === "PRODUCT_FITMENT_PAGE" && <meta name="robots" content="noindex" />}

        {pageType !== "PRODUCT_FITMENT_PAGE" && <meta name="robots" content="all" />}

        <meta property="og:locale" content="en_CA" />
        <meta property="og:locale:alternate" content="en_CA" />
        <meta property="og:locale:alternate" content="fr_CA" />
        <meta property="og:title" content={pageTitle} />

        <meta property="og:description" content={metaDescription} />

        <meta property="og:type" content="product" />

        <meta property="og:site_name" content="PartsAvatar.ca" />
        <meta property="og:url" content={url} />

        <meta property="og:image" content={image} />

        <meta name="twitter:title" content={pageTitle} />

        <meta name="twitter:description" content={metaDescription} />

        <meta name="twitter:image" content={image} />

      </Head>
      <BreadcrumbsMetaHead {...{ breadCrumbList: breadCrumbsList }} />
      <SiteLinkMeta />
      <FAQMetaHead contentIds={staticContent?.links?.contentIds || []} />
      <OrganizationMetaHeader />
      <LocalBusinessMeta />
      <VideosMetaData videos={[...videos, ...productLineVideos, ...manufacturerVideos]} />

    </>
  );
}

export default ProductMetaHead;
